import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { DownloadService, Endpoint, shouldRenderMobile, ToastService } from '@philips-emr/hpms-provider-commons';
import { Button, CheckboxAPI, DateFieldAPI, DeckAPI, FetchAPI, Field, FieldAPI, FileUploaderAPI, FormAPI, PanelAPI, Text } from '@philips-emr/tws-components';
import { ButtonTheme, Placement } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { generateUUID, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { DocumentsDetailsAliases as Aliases, FragmentNames } from '../codegen';

import { DocumentDateStatus } from './accreditation-update/accreditation-documents-controller';
import BaseController from './BaseController';
import { Document, getStampDocumentByStatus } from './documents-controller';

@Controller({ fragment: FragmentNames.DOCUMENTS_DETAILS })
class DocumentsDetailsController extends BaseController {

  @Inject(Aliases.FIELD_EMISSION_DATE)
  private fieldEmissionDate: FieldAPI;
  @Inject(Aliases.DATE_FIELD_VALIDITY)
  private dateFieldValidity: DateFieldAPI;
  @Inject(Aliases.CHECKBOX_VALIDITY)
  private checkboxUndeterminedValidity: CheckboxAPI;
  @Inject(Aliases.FIELD_VALIDITY)
  private fieldValidity: FieldAPI;
  @Inject(Aliases.PANEL_MODAL_DOCUMENTS)
  private panelModalDocs: PanelAPI;
  @Inject(Aliases.FIELD_DISABLED)
  private fieldValidityDisabled: FieldAPI;
  @Inject(Aliases.FIELD_EMISSION_DISABLED)
  private fieldEmissionDisabled: FieldAPI;
  @Inject(Aliases.FIELD_GUIDANCE)
  private fieldGuidance: FieldAPI;
  @Inject(Aliases.FETCH_DOCUMENTS)
  public fetchDocument: FetchAPI;

  @OnMount(Aliases.FETCH_DOCUMENTS)
  onMountFetchDocs(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqDocumentType } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqDocumentType });
    api.forceFetch();
  }

  @OnMount(Aliases.FIELD_NO_DOC)
  onMountNoDoc(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const data = api.getData();
    const { noDocs } = data;
    api.setVisible(noDocs == 'S');
  }

  @OnMount([
    Aliases.FIELD_ISSUE_DATE,
    Aliases.FIELD_ICON,
    Aliases.CONTENT_FIELD_REFUSAL_OR_EXPIRATION_DATE])
  onMountDate(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const data = api.getData();
    const { noDocs, dtIssue, dtRefusal } = data;
    if (api.getAlias() === Aliases.FIELD_ISSUE_DATE && !dtIssue) {
      api.setLabel(MessageService.translate(' '));
    }

    if (api.getAlias() === Aliases.FIELD_ISSUE_DATE && dtRefusal) {
      api.setVisible(false);
      return;
    }

    api.setVisible(noDocs != 'S');
  }

  @OnMount(Aliases.FIELD_REFUSAL)
  onMountRegusal(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const data = api.getData();
    const { noDocs, notes } = data;
    api.setVisible(noDocs != 'S' && notes != null);
  }

  @OnSelectItem(Aliases.DECK_DOCUMENTS)
  onSelectItem(event: SelectItemEvent<Document>): void {
    const { nrSeqProviderDoc, fileName, dsDocument } = event.item;
    const api = event.target as unknown as DeckAPI;
    api.unselectAll();
    if (nrSeqProviderDoc) {
      DownloadService.of({
        endpoint: Endpoint.GET.DOCUMENT_DOWNLOAD,
        name: fileName,
        parameters: { nrSeqProviderDoc, dsDocument },
      });
    }
  }

  @OnDynamicContentRender(Aliases.FIELD_BUTTOM)
  onRenderButtom(event: RenderEvent): void {
    const { render, data } = event;
    const { sendDocument } = data as Document;
    const content = (): ReactElement =>
      <Button onClick={() => { this.openModalDocuments(data as Document); }}
        icon="/static/assets/DLS_Upload.svg"
        value={!shouldRenderMobile() ? MessageService.translate('provider.send-document') : MessageService.translate('provider.sent')}
        theme={ButtonTheme.QUIET_EMPHASIS} iconPlacement={Placement.RIGHT}
        visible={sendDocument == 'S'}>
      </Button>;
    render(content);
  }

  @OnMount(Aliases.FETCH_DOCUMENTS_MODAL)
  onMountFetchDocsModal(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqDocumentType } = this.context.getBulkData();
    api.setParameters({ nrSeqDocumentType });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_DOCUMENTS_MODAL)
  onAfterFetch(event: FetchEvent): void {
    if (event.data) {
      const data = event.data;
      const { dtEmissionParam, dtValidityParam, guidance } = data as Document;

      this.fieldGuidance.setVisible(!isEmptyOrBlankString(guidance));

      switch (dtEmissionParam) {
        case DocumentDateStatus.DO_NOT_REQUEST:
          this.fieldEmissionDate.setVisible(false);
          this.fieldEmissionDisabled.setVisible(true);
          break;
        case DocumentDateStatus.OPTIONAL:
          this.fieldEmissionDate.setVisible(true);
          this.fieldEmissionDisabled.setVisible(false);
          this.fieldEmissionDate.setValidators([{ name: 'prevent-future-date' }] as Validator[]);

          break;
        case DocumentDateStatus.MANDATORY:
          this.fieldEmissionDate.setVisible(true);
          this.fieldEmissionDisabled.setVisible(false);
          this.fieldEmissionDate.setValidators([{ name: 'required', options: {} }, { name: 'prevent-future-date' }] as Validator[]);
          break;
      }

      switch (dtValidityParam) {
        case DocumentDateStatus.DO_NOT_REQUEST:
          this.fieldValidity.setVisible(false);
          this.fieldValidityDisabled.setVisible(true);
          this.checkboxUndeterminedValidity.setEnabled(true);
          break;
        case DocumentDateStatus.OPTIONAL:
          this.fieldValidity.setVisible(true);
          this.fieldValidityDisabled.setVisible(false);
          this.checkboxUndeterminedValidity.setEnabled(true);
          break;
        case DocumentDateStatus.MANDATORY:
        case DocumentDateStatus.MANDATORIY_AND_UNDEFINED:
          this.fieldValidity.setVisible(true);
          this.fieldValidityDisabled.setVisible(false);
          this.checkboxUndeterminedValidity.setEnabled(true);
          this.fieldValidity.setValidators([{ name: 'required', options: {} }, { name: 'prevent-previous-date' }] as Validator[]);
          break;
      }
    }
  }

  @OnMount(Aliases.FILE_UPLOADER)
  onMountFileUploader(event: MountEvent): void {
    const { dsDocument, nrSeqDocumentType } = this.context.getBulkData();
    const predefinedDocuments = {
      key: nrSeqDocumentType as string,
      value: dsDocument as string,
    };
    const api = event.target as FileUploaderAPI;

    api.setPredefinedFiles([predefinedDocuments]);
  }

  @OnSubmit(Aliases.FORM_SEND_DOCS)
  onSubmitForm(event: SubmitEvent): void {
    const record = event.record;
    const api = event.target as FormAPI;
    const { nrSeqDocumentType } = this.context.getBulkData();
    const body = { ...record };

    HttpService.post<Record<string, Value>>(Endpoint.POST.SEND_DOCUMENT, { nrSeqDocumentType }, { body })
      .then((response) => {
        ToastService.open(response);
        this.fetchDocument.forceFetch();
      })
      .catch((response) => {
        ToastService.open(response);
      }).finally(() => {
        api.setRecord({});
        this.panelModalDocs.setVisible(false);
        this.fetchDocument.forceFetch();
      });

  }

  @OnChange(Aliases.FIELD_CHECKBOX)
  onChangeCheckBox(event: ChangeEvent<{ checked: string }>): void {
    const checked = event.value;

    if (!checked) {
      this.fieldValidity.setValidators([{ name: 'required', options: {} }, { name: 'prevent-previous-date' }] as Validator[]);
      this.fieldValidity.setVisible(true);
      this.fieldValidityDisabled.setVisible(false);
      this.dateFieldValidity.setReadOnly(false);
    } else {
      this.fieldValidity.setData({});
      this.dateFieldValidity.setValue('');
      this.fieldValidity.setState({ showValidations: false });
      this.fieldValidity.removeValidator('required');
      this.fieldValidity.setVisible(false);
      this.fieldValidityDisabled.setVisible(true);
    }

  }

  @OnClose(Aliases.MODAL_DOCS)
  onCloseModalDocs(): void {
    this.panelModalDocs.setVisible(false);
  }

  @OnCancel(Aliases.FORM_SEND_DOCS)
  onCancelSendDocs(): void {
    this.panelModalDocs.setVisible(false);
  }

  @OnDynamicContentRender(Aliases.CONTENT_FIELD_REFUSAL_OR_EXPIRATION_DATE)
  fieldRefusalOrExpirationDate(event: RenderEvent): void {
    const { data, render } = event;
    const { dtRefusal, dtExpiration, undeterminedValidity } = data as Document;
    let labelResult = dtRefusal ? MessageService.translate('provider.refusal-date') : MessageService.translate('provider.validity-date');
    const valueResult = dtRefusal ? dtRefusal : dtExpiration;

    if (!dtRefusal && !dtExpiration) {
      labelResult = '';
    }

    const content = (): ReactElement =>
      <Field uuid={generateUUID()} entry="" label={labelResult} width={5}>
        {undeterminedValidity === 'S' ?
          <Text value={valueResult} />
          :
          <Text value={valueResult} formatters={{ value: { name: 'date' } }} />
        }
      </Field>;
    render(content);
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD_DOCUMENTS)
  onRenderStatus(event: RenderEvent): void {
    if (event.data) {
      const { data, render } = event;
      const { ieStatus, dsStatus } = data as Document;
      const content = (): ReactElement => getStampDocumentByStatus(ieStatus, dsStatus);
      render(content);
    }
  }

  private openModalDocuments(data: Document): void {
    const { nrSeqProvider, nrSeqDocumentType, dsDocument } = data;
    this.context.set({ nrSeqProvider, nrSeqDocumentType, dsDocument });
    this.panelModalDocs.setVisible(true);
  }

}

export { DocumentsDetailsController };
